import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Smartphone } from "lucide-react";

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    try { if (localStorage.getItem("chill_pwa_dismissed")) return; } catch {}

    // Detect iOS
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(ios);

    // Listen for beforeinstallprompt (Android/Chrome/Edge)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 30 seconds on page
      setTimeout(() => setShow(true), 30000);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // For iOS — show instructions after 30 seconds
    if (ios) {
      setTimeout(() => setShow(true), 30000);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === "accepted") {
        setShow(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    try { localStorage.setItem("chill_pwa_dismissed", "1"); } catch {}
  };

  if (dismissed || !show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 60 }}
        className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 w-[360px] max-w-[90vw] rounded-2xl overflow-hidden"
        style={{
          background: "#0c0c0e",
          border: "1px solid rgba(250,204,21,0.2)",
          boxShadow: "0 16px 48px rgba(0,0,0,0.5), 0 0 40px rgba(250,204,21,0.08)",
        }}
        data-testid="pwa-install-prompt"
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FACC15]/10 border border-[#FACC15]/20 flex items-center justify-center shrink-0">
              <Smartphone size={20} className="text-[#FACC15]" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-bold text-sm">Get the ChillRefer App</h4>
              <p className="text-white/40 text-xs mt-1">
                {isIOS
                  ? "Tap the share button, then \"Add to Home Screen\""
                  : "Install for quick access — works like a native app"}
              </p>
            </div>
            <button onClick={handleDismiss} className="text-white/30 hover:text-white/60 shrink-0">
              <X size={16} />
            </button>
          </div>

          {!isIOS && deferredPrompt && (
            <button
              onClick={handleInstall}
              className="mt-3 w-full py-2.5 bg-[#FACC15] text-black font-bold text-sm rounded-lg hover:bg-[#eab308] transition-all flex items-center justify-center gap-2"
              data-testid="pwa-install-btn"
            >
              <Download size={16} />
              Install App
            </button>
          )}

          {isIOS && (
            <div className="mt-3 p-2.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white/50 text-center">
              Tap <span className="inline-block mx-1"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline text-[#FACC15]"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></span> then <strong className="text-white/70">"Add to Home Screen"</strong>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
